/**
 * Ikaros header: language toggle and account placement.
 *
 * WHY JAVASCRIPT AND NOT SETTINGS
 * Horizon's header offers a country picker and a language picker, both as
 * dropdowns, and no way to render a language toggle as a single glyph.
 * Both native pickers are switched off in header-group.json; this puts back
 * only what is wanted.
 *
 * THE TOGGLE IS A DESTINATION, NOT A MENU
 * With exactly two locales a dropdown is a control with one useful option
 * in it. So the header shows the language you are NOT reading - EN while
 * you are in Arabic, ع while you are in English - and one tap switches.
 * The label is the destination, which is why it is not the current state.
 *
 * HOW THE SWITCH IS DONE
 * Shopify serves the secondary locale under a path prefix, so the switch is
 * a URL rewrite: add /ar, or strip it. That keeps the customer on the page
 * they were reading instead of dropping them on the home page, and it needs
 * no form post. Query string and hash are preserved.
 *
 * WHERE IT IS INSERTED
 * <header-actions> is a custom element with no class of its own, which is
 * why an earlier build using closest('.header-actions') found nothing and
 * silently rendered no toggle. The tag name is the stable hook.
 *
 * ACCOUNT MOVES TO THE MENU
 * The account icon is hidden in the header by CSS and re-added as a link
 * inside the menu drawer. It is a page people visit rarely and deliberately,
 * so it does not need one of the four slots a phone header has. The header
 * markup is Horizon's, so the icon is hidden rather than removed - deleting
 * it would mean forking the section.
 */

(() => {
  const AR = 'ar';

  const currentLocale = () =>
    (document.documentElement.getAttribute('lang') || 'en').split('-')[0].toLowerCase();

  /* The other language, and where it lives. */
  function target() {
    const path = window.location.pathname;
    const rest = window.location.search + window.location.hash;

    if (currentLocale() === AR) {
      const stripped = path.replace(/^\/ar(?=\/|$)/, '') || '/';
      return { href: stripped + rest, label: 'EN', title: 'English', lang: 'en' };
    }
    return {
      href: '/' + AR + (path === '/' ? '' : path) + rest,
      label: '\u0639',
      title: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629',
      lang: AR,
    };
  }

  function build(cls) {
    const t = target();
    const a = document.createElement('a');
    a.className = cls;
    a.href = t.href;
    a.setAttribute('lang', t.lang);
    a.setAttribute('title', t.title);
    a.setAttribute('aria-label', t.title);
    a.textContent = t.label;
    return a;
  }

  /* First slot in the header's action row, before search and cart. */
  function intoHeader() {
    const host = document.querySelector('header-actions');
    if (!host || host.querySelector('.iklang')) return;
    host.insertBefore(build('iklang iklang--header'), host.firstElementChild);
  }

  /* Menu drawer: account and language together, below the navigation. */
  function intoDrawer() {
    const nav = document.querySelector('.menu-drawer__navigation');
    if (!nav || !nav.parentElement) return;
    if (nav.parentElement.querySelector('.iklang-drawer')) return;

    const wrap = document.createElement('div');
    wrap.className = 'iklang-drawer';
    const arabicUi = currentLocale() === AR;

    const account = document.createElement('a');
    account.className = 'iklang-drawer__link';
    account.href = arabicUi ? '/ar/account' : '/account';
    account.textContent = arabicUi ? '\u0627\u0644\u062d\u0633\u0627\u0628' : 'Account';
    wrap.appendChild(account);

    const t = target();
    const lang = document.createElement('a');
    lang.className = 'iklang-drawer__link';
    lang.href = t.href;
    lang.setAttribute('lang', t.lang);
    lang.textContent = t.title;
    wrap.appendChild(lang);

    nav.parentElement.insertBefore(wrap, nav.nextSibling);
  }

  function run() {
    try { intoHeader(); } catch (e) { console.warn('[ikaros-header-lang] header', e); }
    try { intoDrawer(); } catch (e) { console.warn('[ikaros-header-lang] drawer', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* The drawer is rendered lazily on some templates, and Horizon re-renders
     the header group in the theme editor. */
  document.addEventListener('click', () => setTimeout(run, 200));
  ['shopify:section:load', 'shopify:section:select'].forEach((e) =>
    document.addEventListener(e, () => setTimeout(run, 200))
  );
})();
